import { Card, Pagination } from '@quero-delivery/quero-components-web';
import { useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import { Text, useTheme } from '@querodelivery/uni-react';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import { ENUM_STATUS_PRE_REGISTRATION } from '@/_features/motoboys/constants';
import type { MotoboyPreRegistration } from '../../types';
import styles from './pre-registration-list.module.scss';
import type { PreRegistrationListProps } from './types';
import { SHOW_DOCS_VALIDATION } from '../../constants';
import { Skeleton } from '@/_config/lib/react-native-skeleton';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';

const PENDING_PRE_REGISTRATION_STATUSES = [
  ENUM_STATUS_PRE_REGISTRATION.NEW_REGISTER_PEDING_VALIDATION,
  ENUM_STATUS_PRE_REGISTRATION.REPROVED_NEW_DOCUMENTATION_PROVIDED,
];

const REPROVED_PRE_REGISTRATION_STATUSES = [
  ENUM_STATUS_PRE_REGISTRATION.REPROVED_FOR_CHECK,
  ENUM_STATUS_PRE_REGISTRATION.REPROVED_PEDING_RESEND_DOCUMENTATION,
];

function PreRegistrationList({
  isLoadingDeliverymen,
  deliverymen,
  page,
  setPage,
  totalCount,
}: PreRegistrationListProps) {
  const navigate = useNavigate();
  const { color } = useTheme();
  const hasPreRegistrationsData = !isEmpty(deliverymen);

  function getStatusLabel(status: ENUM_STATUS_PRE_REGISTRATION) {
    if (PENDING_PRE_REGISTRATION_STATUSES.includes(status)) {
      return 'Pendente';
    }

    if (REPROVED_PRE_REGISTRATION_STATUSES.includes(status)) {
      return 'Reprovado';
    }

    return null;
  }

  function getStatusColorsByStatus(status: ENUM_STATUS_PRE_REGISTRATION) {
    if (PENDING_PRE_REGISTRATION_STATUSES.includes(status)) {
      return {
        borderColor: color['color-warning-fill'],
        backgroundColor: color['color-warning-surface'],
      };
    }

    if (REPROVED_PRE_REGISTRATION_STATUSES.includes(status)) {
      return {
        borderColor: color['color-danger-fill'],
        backgroundColor: color['color-danger-surface'],
      };
    }

    return {
      border: 'none',
    };
  }

  function renderStatusBadge(deliveryman: MotoboyPreRegistration) {
    const statusLabel = getStatusLabel(deliveryman.preRegistration.status);

    if (!statusLabel) {
      return null;
    }

    return (
      <div
        className={styles.statusBadge}
        style={getStatusColorsByStatus(deliveryman.preRegistration.status)}
      >
        <Text variant="bodySm" tone="supporting">
          {statusLabel}
        </Text>
      </div>
    );
  }

  const handleClick = (deliveryman: MotoboyPreRegistration) => {
    if (SHOW_DOCS_VALIDATION.includes(deliveryman.preRegistration.status)) {
      return navigate(`/precadastro/entregadores/${deliveryman._id}`);
    }

    return navigate(`/entregadores/${deliveryman._id}`);
  };

  return (
    <div className={styles.listContainer}>
      <Card type="none" className={styles.containerCard}>
        {isLoadingDeliverymen ? (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
          >
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} height={56} borderRadius={32} />
            ))}
          </div>
        ) : hasPreRegistrationsData ? (
          <div className={styles.containerList}>
            <span className={styles.spanResult}>
              {totalCount} resultados encontrados
            </span>
            {deliverymen?.map(deliveryman => (
              <div
                key={deliveryman._id}
                onClick={() => handleClick(deliveryman)}
                className={styles.itemList}
              >
                <div className={styles.boxName}>
                  <span>{deliveryman.nome}</span>
                  <div className={styles.boxActions}>
                    {renderStatusBadge(deliveryman)}
                    <FontAwesomeIcon icon={faAngleRight} />
                  </div>
                </div>
              </div>
            ))}
            <div data-testid="pagination" className={styles.pagination}>
              <Pagination
                limitPerPage={DEFAULT_LIMIT_REQUEST}
                currentPage={page}
                totalCount={totalCount || 0}
                onPageChange={setPage}
              />
            </div>
          </div>
        ) : (
          <TableEmptyState
            title="Nenhum pré-cadastro encontrado"
            description="Não encontramos pré-cadastros com os filtros selecionados."
          />
        )}
      </Card>
    </div>
  );
}

export { PreRegistrationList };
